import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import {
	Card,
	CardBody,
	CardHeader,
} from "@tql/ui-components-react/kendo/layout";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { mail_01 } from "@tql/ui-components-react/untitled/icons";
import { useAtomValue } from "jotai";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";

import { SearchCombobox } from "~/shared/components/fields/search-combobox.tsx";
import type { LtlOwner } from "~/shared/schemas/customer-schema.ts";
import { ltlOwnerSchema } from "~/shared/schemas/customer-schema.ts";
import type { ShipmentSalesContacts } from "~/shared/schemas/shipment-management-schema";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";

import { isEditingShipmentAtom } from "../../state/shipment-edit-atoms.ts";

const getLtlBrokersRoute = (searchTerm: string) =>
	apiRoutes.GETLTLBROKERS.replace(":userInput", encodeURIComponent(searchTerm));

export const TqlSalesContacts = ({
	details = {
		lae: { name: "", phone: "", extension: "", email: "" },
		ltlar: { name: "", phone: "", extension: "", email: "" },
	},
}: {
	details?: ShipmentSalesContacts;
}) => {
	const isEditing = useAtomValue(isEditingShipmentAtom);
	// LTL AR editing remains disabled until the future enhancement is implemented.
	const isLtlArFieldEditable = false;

	const [selectedOwner, setSelectedOwner] = useState<LtlOwner | null>(null);

	useEffect(() => {
		if (!isEditing) {
			setSelectedOwner(null);
		}
	}, [isEditing]);

	const ltlarContact: ShipmentSalesContacts["ltlar"] = {
		name: selectedOwner?.name ?? details.ltlar.name,
		phone: details.ltlar.phone,
		extension: selectedOwner?.extension ?? details.ltlar.extension,
		email: selectedOwner?.email ?? details.ltlar.email,
	};

	const renderContactSection = (
		title: string,
		testId: string,
		contact: ShipmentSalesContacts[keyof ShipmentSalesContacts],
		nameEditor?: ReactNode,
	) => (
		<div className={"sales-contact"} data-testid={testId}>
			<span className={"sales-contact-role"}>{title}</span>
			<div className={"sales-contact-grid"}>
				<div className={"section-label"}>Contact Name</div>
				<div className={"section-label"}>Phone</div>
				<div className={"section-label"}>EXT</div>
				{nameEditor ?? (
					<div data-testid={"sales-contact-name"}>{contact.name ?? "N/A"}</div>
				)}
				<div data-testid={"sales-contact-phone"}>{contact.phone ?? "N/A"}</div>
				<div data-testid={"sales-contact-extension"}>
					{contact.extension ?? "N/A"}
				</div>
			</div>
			{contact.email ? (
				<div className={"sales-contact-email"}>
					<Button
						aria-label={`Email ${contact.email}`}
						onClick={() => {
							globalThis.location.href = `mailto:${contact.email}`;
						}}
						startIcon={<SvgIcon icon={mail_01} />}
						variant={"tertiary"}
					>
						{contact.email}
					</Button>
				</div>
			) : null}
		</div>
	);

	return (
		<Card
			className={"full-height shipment-info-card"}
			data-testid={"shipment-management-sales-contacts"}
		>
			<CardHeader>
				<h3>TQL Sales Contacts</h3>
			</CardHeader>
			<CardBody>
				<div className={"sales-contacts"}>
					{isLtlArFieldEditable
						? renderContactSection(
								"LTL AR",
								"sales-contact-ltl-ar",
								ltlarContact,
								<div>
									<SearchCombobox
										dataItemKey={"userId"}
										handleChange={(event) => {
											const owner = event?.value as LtlOwner | null;
											if (!owner) {
												setSelectedOwner(null);
												return;
											}
											setSelectedOwner({
												...owner,
												email: owner.email ? `${owner.email}@tql.com` : "",
											});
										}}
										hint={false}
										hintDirection={undefined}
										id={"ltlar-userId"}
										label={""}
										onClear={() => setSelectedOwner(null)}
										route={getLtlBrokersRoute}
										schema={ltlOwnerSchema}
										textField={"name"}
										value={
											selectedOwner ?? {
												userId: 0,
												name: details.ltlar.name ?? "",
												email: details.ltlar.email
													? `${details.ltlar.email}@tql.com`
													: "",
												extension: details.ltlar.extension ?? "",
											}
										}
										wrapperStyle={{ minWidth: "12rem" }}
									/>
								</div>,
							)
						: renderContactSection(
								"LTL AR",
								"sales-contact-ltl-ar",
								details.ltlar,
							)}
					{renderContactSection("LAE", "sales-contact-lae", details.lae)}
				</div>
			</CardBody>
		</Card>
	);
};
